import React from 'react';

const Kemforge = () => (
  <div className="container mx-auto px-4 py-8">
    {/* Hero */}
    <header className="mb-8">
      <h1 className="text-3xl font-bold mb-3">Kemforge</h1>
      <h2 className="text-3xl mb-4">Curl-compatible requests with post-quantum visibility</h2>
      <p className="text-lg text-gray-700">A practical command-line tool for developers and security teams who want familiar curl-style usability together with clear, built-in post-quantum cryptography (PQC) visibility.</p>
    </header>

    {/* ...existing code... */}

    {/* What is Kemforge */}
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">What is Kemforge?</h2>
      <p className="mb-4">Kemforge is a modern, curl-compatible command-line tool for making web requests that includes native support for Post-Quantum Cryptography (PQC). It is intended as a practical tool for developers, DevOps and security teams who need to exercise HTTP/HTTPS endpoints while also seeing whether connections are negotiating post-quantum key exchange.</p>
      <p className="mb-4">Because Kemforge follows a familiar curl-style interface, it fits naturally into existing scripts, tests and developer workflows — you get the same productivity you expect from a CLI tool, with the added benefit of PQC-aware reporting.</p>
      {/* Screenshot / image showing Kemforge output */}
      <div className="mb-6 text-center">
        <img
          src="/GoogleKemforgeWebsite.png"
          alt="Kemforge example output (Google)"
          className="mx-auto rounded shadow-md"
          style={{ maxWidth: '760px', width: '100%', height: 'auto' }}
        />
      </div>
    </section>

    {/* Video explainer */}
    <section className="mb-8">
      <h3 className="text-xl font-semibold mb-3">Watch a short explainer</h3>
      {/* Option A: padding-top responsive wrapper (recommended, works without Tailwind plugins) */}
      <div className="mb-6">
          <div className="max-w-3xl mx-auto mb-4">
          <div className="relative" style={{ paddingTop: '56.25%' }}>
            <iframe
              title="Kemforge explainer - Option A"
              className="absolute top-0 left-0 w-full h-full rounded shadow-sm"
              src="https://www.youtube.com/embed/F9_9aZEhURQ"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </div>

    </section>

    {/* Why it matters */}
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">Why it matters</h2>
      <p className="mb-3">Traditional key-exchange mechanisms used in TLS are vulnerable to future quantum attacks. As post-quantum algorithms become available, organisations will need clear visibility into whether their HTTPS endpoints actually negotiate post-quantum protections in practice — not just in theory.</p>
      <p className="mb-3">Kemforge makes that visibility practical and easy: it helps teams verify negotiated key exchange metadata, identify when endpoints are not using PQC, and take immediate, actionable steps to reduce exposure.</p>
    </section>

    {/* What makes it unique */}
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">What makes Kemforge unique</h2>
      <ul className="list-disc pl-5 mb-4">
        <li className="mb-2"><strong>Post-quantum focus:</strong> Kemforge inspects and reports on PQC negotiation rather than only performing traditional TLS inspection.</li>
        <li className="mb-2"><strong>Built-in PQC support:</strong> It provides native PQC capability instead of relying on the OS TLS library version, ensuring consistent behaviour across platforms.</li>
        <li className="mb-2"><strong>TLS metadata reporting:</strong> For HTTPS requests Kemforge reports detailed TLS metadata so you can immediately see what was negotiated.</li>
        <li className="mb-2"><strong>ML-KEM detection:</strong> Kemforge indicates whether the negotiated key exchange includes ML-KEM-based post-quantum cryptography.</li>
        <li className="mb-2"><strong>Actionable warnings:</strong> If post-quantum cryptography is not negotiated, Kemforge warns you so results are immediately actionable.</li>
        <li className="mb-2"><strong>Curl-compatible workflow:</strong> Kemforge behaves like a familiar curl-style CLI tool and supports common web-request workflows, so adoption is straightforward for teams who already use curl.</li>
      </ul>
    </section>

    {/* Installation */}
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">Installation</h2>

      <h3 className="text-xl font-semibold mb-2">Windows</h3>
      <pre className="bg-gray-100 p-3 rounded overflow-x-auto mb-4"><code>winget install ConnectingApps.Kemforge</code></pre>

      <h3 className="text-xl font-semibold mb-2">macOS</h3>
      <p className="mb-2">Kemforge can be installed via <a href="https://brew.sh" className="text-blue-600 hover:underline">Homebrew</a>. Open Terminal and run:</p>
      <pre className="bg-gray-100 p-3 rounded overflow-x-auto mb-2"><code>brew tap ConnectingApps/kemforge</code></pre>
      <pre className="bg-gray-100 p-3 rounded overflow-x-auto mb-4"><code>brew install kemforge</code></pre>

      <h3 className="text-xl font-semibold mb-2">Ubuntu / Debian</h3>
      <ol className="list-decimal pl-5 mb-4">
        <li className="mb-3">Install Go (if not already installed):
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto mt-2"><code>sudo apt update</code></pre>
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto mt-4"><code>sudo apt install -y golang-go</code></pre>
        </li>
        <li className="mb-3">Ensure the Go bin directory is in your PATH:
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto mt-2"><code>echo 'export PATH="$PATH:$(go env GOBIN):$(go env GOPATH)/bin"' >> ~/.bashrc</code></pre>
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto mt-4"><code>source ~/.bashrc</code></pre>
        </li>
        <li className="mb-3">Install kemforge:
          <pre className="bg-gray-100 p-3 rounded overflow-x-auto mt-2"><code>go install github.com/ConnectingApps/kemforge@latest</code></pre>
        </li>
      </ol>

      <h4 className="text-lg font-semibold mb-2">After installation</h4>
      <p className="mb-2">Once installed, Kemforge can be used like curl. For example:</p>
      <pre className="bg-gray-100 p-3 rounded overflow-x-auto"><code>kemforge -I https://www.google.com</code></pre>
    </section>

    {/* Closing CTA */}
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-3">Try Kemforge</h2>
      <p className="mb-3">If you or your team are responsible for web application security, migration planning or compliance, Kemforge gives quick, reliable visibility into whether your HTTPS endpoints are negotiating post-quantum protection. It combines the command-line familiarity of curl with PQC-aware reporting so you can integrate checks into CI, test suites and operational runbooks.</p>
      <p className="mb-3"><strong>Install Kemforge and start checking whether your HTTPS connections are ready for the post-quantum era.</strong></p>
    </section>

    {/* Attribution */}
    <section className="mb-8">
      <h2 className="text-2xl font-semibold mb-2">Attribution</h2>
      <p className="mb-2">Kemforge was developed by <a href="https://quantumsafeaudit.com" className="text-blue-600 hover:underline">QuantumSafeAudit.com</a> and <a href="https://github.com/ConnectingApps" className="text-blue-600 hover:underline">Connecting Apps</a>. The code implementation was done by <strong>Daan Acohen</strong>.</p>
    </section>
  </div>
);

export default Kemforge;
